/**
 * Advisory Initial State
 *
 * `AdvisoryState` carries per-channel health only, by construction. This is
 * the direct structural fix for a bug that has already shipped once in
 * this codebase: one global `loading`/`error` flag leaking across
 * concurrently-listened items — both `chatInitialState.ts`'s `ChatState`
 * and an earlier reference implementation's state shape independently
 * reached for the same anti-pattern. `AdvisoryState` has NO top-level
 * `loading` field and NO top-level `error` field; every listener's health
 * lives on its own `AdvisoryChannelStatus` record instead, so a denial or
 * failure on one channel can never be mistaken for the status of another.
 */

import type {
  AdvisoryChannelStatus,
  EngagementSummaryDoc,
  KeyQuestionDoc,
  StrategicAdvisoryDoc,
} from './advisoryTypes';

/**
 * One engagement's client-held state: its goal document, its one
 * subcollection, and one `AdvisoryChannelStatus` per per-project channel
 * kind. The `engagements` list channel is NOT here — it has no
 * `projectId` and lives on `AdvisoryState.engagementsChannel` directly.
 */
export interface AdvisoryEngagementEntry {
  goalDoc: StrategicAdvisoryDoc | null;
  keyQuestions: KeyQuestionDoc[];
  channels: Record<'goal' | 'keyQuestions', AdvisoryChannelStatus>;
  /**
   * The recently-mutated guard, per project (this entry) and per mutable
   * channel — id -> expiry (epoch ms). Only `keyQuestions` carries an entry
   * here; `goal` has no merge-by-id concept (it is a single document, not
   * a collection) and is never polled. An id present in this map with
   * `expiresAt > now` is "guarded": a poll/snapshot merge must neither
   * resurrect it (if it was just optimistically removed) nor delete it (if
   * it was just optimistically added and the fetch hasn't caught up yet)
   * until the guard expires. Plain serializable values only — never a
   * `Map` or `Set` (RTK's serializability middleware flags both). Dies
   * with the entry on `clearEngagement` / `resetAdvisory` — both already
   * discard the whole entry object, so no extra cleanup code is needed for
   * this field.
   *
   * This guard machinery is narrowed to the one mutable channel
   * (`keyQuestions`) — there is no `criteria` channel to guard. See
   * `advisoryThunks.ts`'s own note on why it currently has no production
   * caller but is kept as generic infrastructure.
   */
  recentlyMutated: Record<'keyQuestions', Record<string, number>>;
}

/**
 * The whole Strategic Advisory slice's state. No top-level `loading`, no
 * top-level `error` — see the module header.
 */
export interface AdvisoryState {
  engagementsChannel: AdvisoryChannelStatus;
  engagements: EngagementSummaryDoc[];
  /** Confirmed removals remain hidden if an older list snapshot arrives afterward. */
  deletedEngagementIds: string[];
  byProjectId: Record<string, AdvisoryEngagementEntry>;
}

/**
 * The canonical "no snapshot yet" channel status. Frozen so any accidental
 * direct mutation of this exact reference throws in development rather than
 * silently corrupting every channel that was ever initialised from it.
 * Never assigned directly onto state — every use spreads a fresh copy (see
 * `createAdvisoryEngagementEntry` and `initialAdvisoryState` below), so two
 * independent channels are never aliased to the same object.
 */
export const EMPTY_CHANNEL_STATUS: Readonly<AdvisoryChannelStatus> = Object.freeze({
  firstSnapshotReceived: false,
  isEmpty: true,
  errorCode: null,
});

/**
 * Builds a brand-new `AdvisoryEngagementEntry`, called every time a
 * reducer needs to lazily create a project's entry. Returns a genuinely
 * fresh object graph on every call — each of the two channel statuses is
 * its own spread copy of `EMPTY_CHANNEL_STATUS`, never a shared reference —
 * so that two different projects' channels can never alias one another
 * through Immer's copy-on-write drafting.
 */
export function createAdvisoryEngagementEntry(): AdvisoryEngagementEntry {
  return {
    goalDoc: null,
    keyQuestions: [],
    channels: {
      goal: { ...EMPTY_CHANNEL_STATUS },
      keyQuestions: { ...EMPTY_CHANNEL_STATUS },
    },
    recentlyMutated: { keyQuestions: {} },
  };
}

/** The advisory slice's initial state. */
export const initialAdvisoryState: AdvisoryState = {
  engagementsChannel: { ...EMPTY_CHANNEL_STATUS },
  engagements: [],
  deletedEngagementIds: [],
  byProjectId: {},
};
